import { PriceFunc, PriceElemsData, PriceSegment } from '@icc/shared/types/price/Prices';
import { Injectable } from '@angular/core';
import { Common } from '@icc/shared/helpers';
import { TaxRatesService } from '@icc/b2b/shared';
import { exchange } from '@icc/shared';
import { ComplexConfiguratorField, ComplexConfiguratorFieldConfig } from '@icc/shared/types/complex/ComplexConfiguratorField';
import * as Sentry from '@sentry/angular-ivy';

@Injectable()
export class PriceComplexService {
    constructor() {}

    /**
     * Wylicza cenę kompleksowego konfiguratora
     * @param  {object} conf          Konfiguracja
     * @param  {object} PriceElems    Wycena
     * @param  {object} NoPriceCauses Powody braku ceny
     * @return {number}               Cena po dopłatach
     */
    @PriceFunc({
        shortName: 'fields',
        data: {
            configuratorId: 'conf.configuratorId',
            fields: 'conf.fields',
            prices: 'prices',
            priceRasters: 'priceRasters',
            pricesSettings: 'pricesSettings',
            conf: 'conf',
        },
    })
    allFieldsPrice(
        { NoPriceCauses }: PriceElemsData,
        { configuratorId, fields, prices, priceRasters, pricesSettings, conf }
    ): PriceSegment[] {
        const priceSegments: PriceSegment[] = [];
        const matchingPrices = ((priceRasters || {})[configuratorId] || []).filter((price) =>
            price.definition.every((el) =>
                Object.values<ComplexConfiguratorField>(fields).some((field) => Number(field.featureId) === Number(el.featureId))
            )
        );
        if (matchingPrices) {
            matchingPrices.forEach((price) => {
                for (const raster of price.raster) {
                    const features = [];
                    const values = {};
                    const isMatching = raster.d.every((feature, i) => {
                        const featureId = price.definition[i]?.featureId;
                        const confFeature = Object.values<ComplexConfiguratorField>(fields).find(
                            (el) => Number(el.featureId) === Number(featureId)
                        );
                        let value = feature[0];
                        features.push(featureId);
                        values[featureId] = value;

                        let confFeatureValue = confFeature.value;

                        if (confFeature.type === 'select' || confFeature.type === 'select_number' || confFeature.type === 'select_product') {
                            confFeatureValue = String(Number(confFeature.value));
                            value = String(feature[0]);
                        } else if (confFeature.type === 'number') {
                            value = feature.length === 1
                                ? Number(feature[0])
                                : {
                                    min: Number(feature[0]),
                                    max: Number(feature[1]),
                                    step: feature[2] && Number(feature[2]),
                                };
                            confFeatureValue = Number(confFeature.value);
                        }

                        return Common.isObject(value)
                            ? confFeatureValue >= value.min &&
                                  confFeatureValue <= value.max &&
                                  (!value.step || Number(confFeatureValue) % value.step === 0)
                            : confFeatureValue === value;
                    });
                    if (isMatching) {
                        if (price.tax_rate === undefined || price.tax_rate === null) {
                            Sentry.captureMessage('Błąd: Brak stawki VAT przy wyliczaniu ceny w konfiguratorze uniwersalnym', {
                                level: 'error',
                                extra: {
                                    price,
                                    conf
                                }
                            });
                        }
                        priceSegments.push({
                            type: 'productFeaturesRaster',
                            baseValue: raster.p,
                            value: raster.p,
                            valueCurrency: price.currency,
                            valueIsGross: price.is_gross,
                            taxRate: price.tax_rate,
                            valueType: 'value',
                            data: {
                                id: price.id,
                                features,
                                values,
                            },
                        });
                        break;
                    }
                }
            });
        }

        Object.values<ComplexConfiguratorField>(fields).forEach((feature) => {
            let price;
            if (feature.price?.value) {
                price = {
                    value: feature.price.value,
                    is_gross: feature.price.isGross,
                    tax_rate: feature.price.taxRate,
                    currency: feature.price.currency,
                };
            } else {
                price = prices?.find(
                    (el) =>
                        feature.featureId === el.product_feature_id &&
                        Number(feature.value) === el.product_feature_option_id
                );
            }

            if (price) {
                priceSegments.push({
                    type: 'productFeature',
                    baseValue: price.value,
                    value: price.value,
                    valueCurrency: price.currency,
                    valueIsGross: price.is_gross,
                    taxRate: price.tax_rate,
                    valueType: 'value',
                    data: {
                        id: price.id,
                        feature: feature.featureId,
                        value: feature.value,
                    },
                });
            }
        });

        if (priceSegments.length) {
            return priceSegments;
        }

        NoPriceCauses.push('no prices for features');

        return [
            {
                type: 'productFeaturesRaster',
                baseValue: null,
                value: null,
                valueType: 'value',
                data: {},
            },
        ];
    }
}
